import React from 'react';
import { cn } from '../../../lib/utils';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../../components/ui/table';
import { Edit, Loader2, Trash2 } from 'lucide-react';
import { FounderTransactionKind } from '../../../api/treasury';
import { TransactionsTableProps } from '../types';

export const KIND_LABELS: Record<FounderTransactionKind, string> = {
  contribution: 'Взнос на счёт',
  reimbursement: 'Возврат вклада',
  profit: 'Доля прибыли',
};

const KIND_CLASSES: Record<FounderTransactionKind, string> = {
  contribution: 'bg-sky-500/10 text-sky-500',
  reimbursement: 'bg-amber-500/10 text-amber-500',
  profit: 'bg-emerald-500/10 text-emerald-500',
};

export function TransactionsTable({
  isDark,
  transactions,
  isLoading,
  onEdit,
  onDelete,
  formatMoney,
}: TransactionsTableProps) {
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-[#C9A227]" />
      </div>
    );
  }

  const th = cn('font-semibold', isDark ? 'text-slate-400' : 'text-slate-500');
  const rowCls = cn(
    'transition-colors',
    isDark ? 'border-slate-700 hover:bg-slate-800/50' : 'border-slate-100 hover:bg-slate-50',
  );

  return (
    <Table>
      <TableHeader>
        <TableRow className={rowCls}>
          <TableHead className={th}>Дата</TableHead>
          <TableHead className={th}>Основатель</TableHead>
          <TableHead className={th}>Вид</TableHead>
          <TableHead className={th}>Сумма</TableHead>
          <TableHead className={th}>Комментарий</TableHead>
          <TableHead className={th}>Действия</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {transactions.length === 0 ? (
          <TableRow>
            <TableCell colSpan={6} className="text-center py-12 text-slate-500">
              Движений пока нет — деньги со счёта никто не брал и не вносил
            </TableCell>
          </TableRow>
        ) : (
          transactions.map((transaction) => (
            <TableRow key={transaction.id} className={rowCls}>
              <TableCell className={cn(isDark ? 'text-slate-300' : 'text-slate-700')}>
                {transaction.date}
              </TableCell>
              <TableCell className={cn('font-medium', isDark ? 'text-white' : 'text-[#111111]')}>
                {transaction.payer?.name || '—'}
              </TableCell>
              <TableCell>
                <Badge className={cn('rounded-lg font-medium border-0', KIND_CLASSES[transaction.kind])}>
                  {KIND_LABELS[transaction.kind]}
                </Badge>
              </TableCell>
              <TableCell className={cn('font-semibold', isDark ? 'text-white' : 'text-[#111111]')}>
                {formatMoney(transaction.amount)}
              </TableCell>
              <TableCell className={cn('max-w-xs truncate', isDark ? 'text-slate-400' : 'text-slate-500')}>
                {transaction.comment || '—'}
              </TableCell>
              <TableCell>
                <div className="flex items-center gap-2">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => onEdit(transaction)}
                    className={cn('h-8 w-8', isDark ? 'hover:bg-slate-700' : 'hover:bg-slate-100')}
                  >
                    <Edit className="w-4 h-4 text-slate-400" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => onDelete(transaction.id)}
                    className={cn('h-8 w-8', isDark ? 'hover:bg-slate-700' : 'hover:bg-slate-100')}
                  >
                    <Trash2 className="w-4 h-4 text-red-500" />
                  </Button>
                </div>
              </TableCell>
            </TableRow>
          ))
        )}
      </TableBody>
    </Table>
  );
}
